/**
 * LibraryPick.jsx
 * Choosing one picture from the brand's library to start or end a video.
 *
 * Unlike LibraryImagePicker it hands back the library record, not a file: the
 * Worker gives Replicate a signed link to the picture already in R2, so there
 * is nothing to download and upload again.
 *
 * Dependencies: services/api.js
 */

import { useEffect, useState } from 'react';
import { api } from '../../services/api.js';
import { Warning } from '../Icons.jsx';

/** What P-Video accepts. GIF and AVIF are in the library but not usable here. */
const USABLE = ['image/jpeg', 'image/png', 'image/webp'];

export default function LibraryPick({ brandId, onClose, onPick }) {
  const [media, setMedia] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.media
      .list(brandId)
      .then(({ media: all }) => setMedia(all.filter((m) => m.kind === 'image' && !m.archived && USABLE.includes(m.mime))))
      .catch(setError);
  }, [brandId]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal modal--wide"
        role="dialog"
        aria-label="Choose a picture"
        onClick={(e) => e.stopPropagation()}
      >
        <h2>Choose a picture</h2>
        <p className="modal__sub">From this brand’s library. JPEG, PNG and WebP pictures only.</p>

        {error && (
          <div className="notice notice--error">
            <Warning />
            <span>{error.message}</span>
          </div>
        )}

        {!media && !error && <p className="mono">Loading…</p>}
        {media && media.length === 0 && <p className="modal__sub">No pictures in the library yet.</p>}

        {media && media.length > 0 && (
          <div className="vid-lib">
            {media.map((m) => (
              <button type="button" key={m.id} className="lib-item" onClick={() => onPick(m)}>
                <img src={api.media.rawUrl(m.id)} alt={m.alt_text || ''} loading="lazy" />
                <span className="lib-item__meta">
                  <span>{m.aspect ?? ''}</span>
                  <span>{m.width && m.height ? `${m.width}×${m.height}` : ''}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="modal__actions btn-row">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
